import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BookOpen,
  Crown,
  Sparkles,
  ArrowRight,
  Star,
  Flame,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTHOR_IMAGE =
  'https://customer-assets.emergentagent.com/job_e5aeb861-4698-4c3f-a54b-4b0b2b4402c1/artifacts/zvomh038_FB_IMG_1780263257283.jpg';

export default function WelcomeHomePage() {
  const [collective, setCollective] = useState(null);

  useEffect(() => {
    axios.get(`${API}/membership/package`)
      .then(({ data }) => setCollective(data))
      .catch(() => {});
  }, []);

  const foundingLeft = collective?.founding_members_left ?? 50;
  const currentPrice = collective?.current_price_usd ?? 48.5;
  const isFoundingOpen = collective?.current_tier === 'founding';

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="welcome-home-root">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-silver/15">
        <div className="absolute inset-0 bg-gradient-to-br from-maroon/15 via-background to-secondary/10 pointer-events-none" />
        <div className="container relative mx-auto px-4 py-16 md:py-24 max-w-6xl">
          <div className="grid md:grid-cols-[1fr,320px] gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-secondary font-semibold mb-4">Breah Kettunen · Magnetic Mind Coach</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] mb-5">
                Taking Your<br/>
                <span className="text-secondary italic">Power Back</span>
              </h1>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed mb-3 max-w-xl">
                The 5-Step Magnetic Mind Method to stop the cycles you can name but can&apos;t break — and start <em>living</em> as the person you&apos;ve been waiting to become.
              </p>
              <p className="text-sm text-muted-foreground italic mb-8">
                Two doors below. Walk through the one that&apos;s pulling at you.
              </p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className="border-secondary/30 text-secondary"><Star className="w-3 h-3 mr-1 inline" /> Trusted by hundreds of souls</Badge>
                <Badge variant="outline" className="border-secondary/30 text-secondary"><Sparkles className="w-3 h-3 mr-1 inline" /> E-book + practice app + community</Badge>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="hidden md:block"
            >
              <img
                src={AUTHOR_IMAGE}
                alt="Breah Kettunen"
                className="w-full rounded-2xl shadow-2xl shadow-maroon/20 object-cover aspect-[4/5]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* THREE PATHS */}
      <section className="border-b border-silver/15 bg-card/30">
        <div className="container mx-auto px-4 py-16 max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-secondary font-semibold mb-2">Pick your starting point</p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground">Two ways in.</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">

            {/* PATH 1: E-BOOK */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-7 bg-card border-silver/20 hover:border-secondary/50 hover:-translate-y-1 transition-all h-full flex flex-col" data-testid="path-card-ebook">
                <div className="w-12 h-12 rounded-xl bg-secondary/15 flex items-center justify-center mb-5">
                  <BookOpen className="w-6 h-6 text-secondary" />
                </div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">For the reader</p>
                <h3 className="font-heading text-2xl text-foreground leading-tight mb-3">
                  Get the <em className="text-secondary">E-Book</em>
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
                  The complete 5-Step Magnetic Mind Method. Read at your own pace. Yours forever.
                </p>
                <div className="mb-5">
                  <p className="font-heading text-3xl text-foreground">
                    $19.99 <span className="text-base text-muted-foreground line-through ml-1">$59.99</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">One-time · Instant PDF download</p>
                </div>
                <Link to="/ebook" data-testid="path-cta-ebook">
                  <Button className="w-full bg-card hover:bg-secondary/15 text-foreground border border-secondary/40 font-semibold h-11">
                    Read the Book <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </Card>
            </motion.div>

            {/* PATH 3: COLLECTIVE */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-7 bg-gradient-to-br from-maroon/20 to-card border-maroon/40 hover:border-maroon hover:-translate-y-1 transition-all h-full flex flex-col relative" data-testid="path-card-collective">
                {isFoundingOpen && (
                  <Badge className="absolute -top-3 right-5 bg-maroon text-primary-foreground px-3">
                    <Flame className="w-3 h-3 mr-1 inline" />{foundingLeft} Founding Spots
                  </Badge>
                )}
                <div className="w-12 h-12 rounded-xl bg-maroon/20 flex items-center justify-center mb-5">
                  <Crown className="w-6 h-6 text-secondary" />
                </div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Live the work</p>
                <h3 className="font-heading text-2xl text-foreground leading-tight mb-3">
                  Join <em className="text-secondary">The Power Collective</em>
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
                  Weekly live Recodes + 1-on-1 access + private community + everything else. The closest thing to working with me directly.
                </p>
                <div className="mb-5">
                  <p className="font-heading text-3xl text-foreground">
                    ${currentPrice}<span className="text-base text-muted-foreground">/mo</span>
                    {isFoundingOpen && (
                      <span className="text-base text-muted-foreground line-through ml-2">$97</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isFoundingOpen ? 'Founding rate · Locked in forever · Cancel anytime' : 'Monthly · Cancel anytime'}
                  </p>
                </div>
                <Link to="/collective" data-testid="path-cta-collective">
                  <Button className="w-full bg-maroon hover:bg-maroon/90 text-primary-foreground font-semibold h-11">
                    {isFoundingOpen ? 'Claim Founding Spot' : 'Become a Member'} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </Card>
            </motion.div>

          </div>
        </div>
      </section>

      {/* RETURN VISITOR / E-COURSE STRIP */}
      <section className="border-b border-silver/15">
        <div className="container mx-auto px-4 py-10 max-w-4xl text-center">
          <p className="text-sm text-muted-foreground">
            Already have the E-Book?{' '}
            <Link to="/course" className="text-secondary hover:underline font-medium" data-testid="ecourse-link">
              Open your E-Course →
            </Link>
            {'  ·  '}
            Already a member?{' '}
            <Link to="/circle" className="text-secondary hover:underline font-medium" data-testid="circle-link">
              Enter the Collective Portal →
            </Link>
          </p>
          <p className="text-sm text-muted-foreground mt-3">
            Looking for the daily ritual?{' '}
            <Link to="/practice" className="text-secondary hover:underline font-medium" data-testid="practice-link">
              Power Planning Practice App →
            </Link>
          </p>
        </div>
      </section>

      {/* PROMISE BLOCK */}
      <section>
        <div className="container mx-auto px-4 py-16 max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-secondary font-semibold mb-3">A note from me</p>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground leading-tight mb-6">
            You are <em className="text-secondary">not broken.</em><br/>
            You have just been running <em>old programs.</em>
          </h2>
          <p className="text-base md:text-lg text-foreground/85 leading-relaxed mb-3">
            I built this work for the soul who&apos;s done all the right things — the therapy, the books, the programs — and still finds themselves <em>bracing</em>.
          </p>
          <p className="text-base text-muted-foreground leading-relaxed">
            Whether you read the book or join the Collective — you&apos;re not just learning. You&apos;re <em>practicing</em> a life. Welcome.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <span className="text-secondary text-2xl">—</span>
            <span className="font-heading italic text-foreground">Breah</span>
          </div>
        </div>
      </section>
    </div>
  );
}
